<template>
  <v-container fluid class="relative header-space container">
    <div>
      <v-row>
        <v-col sm="12">
          <h1>Geo-Daten Abgleich</h1>
          <p>
            Hier können die Geo-Daten der Accounts mit Hilfe von Google ergänzt werden.<br>
            Pro Durchlauf werden die frischesten 250 Accounts verarbeitet (updatedAt Feld).<br>
            Voraussetzung dafür ist:
          </p>
          <ul>
            <li>Account enthält halbwegs vollständige Addressdaten (PLZ, Stadt, Länderkürzel)</li>
            <li>Account ist approved</li>
            <li>Geo-Daten sind noch nicht vollständig (longitude und/oder latitude sind leer)</li>
          </ul>
          <p class="mt-3">Die neuen Geo-Daten werden sofort beim Account gespeichert, natürlich revisionssicher als Change.</p>
        </v-col>
      </v-row>
      <!-- Controls bar -->
      <v-row class="mb-4">
        <v-col cols="12">
          <div class="controls-inline">
            <v-text-field
              v-model="group"
              label="Gruppe (optional)"
              placeholder="z.B. MED"
              clearable
              dense
            />
            <v-text-field
              v-model="year"
              label="Periode/Jahr (optional)"
              placeholder="z.B. 2024"
              clearable
              dense
              type="text"

            />
            <v-select
              v-model="lang"
              :items="langOptions"
              label="Sprache"
              dense
            />
            <!-- Missing-only select -->
            <v-select
              v-model="missingOnly"
              :items="missingOptions"
              label="Zielmenge"
              dense
            />
            <v-text-field
              v-model.number="debugLimit"
              label="Limit"
              type="number"
              min="1"
              step="1"
              dense
              class="ctrl"
              :rules="[v => (!!v && Number(v) > 0) || 'Limit > 0']"
            />
            <v-btn
              color="primary"
              :disabled="!isOverviewScopeValid"
              :loading="loading"
              @click="getList"
              class="ml-2 ml-auto"
            >
              Übersicht laden
            </v-btn>
          </div>
        </v-col>
      </v-row>
      <v-row v-show="initiated">
        <v-col sm="12">
          <div>
            <v-card class="mb-6">
              <v-simple-table>
                <thead>
                <tr>
                  <th class="text-left column-width">Name</th>
                  <th class="text-left column-width">Wert</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="metric in geoDataInformation" v-bind:key="metric.name">
                  <td>{{ metric.name }}</td>
                  <td>{{ metric.value }}</td>
                </tr>
                </tbody>
              </v-simple-table>
            </v-card>
          </div>
          <div class="text-center">
            <v-btn primary v-on:click="bulkRevise" :disabled="!isOverviewScopeValid">
              <v-progress-circular v-if="geoDataLoading === true" color="grey" class="mr-2" :width="3" indeterminate></v-progress-circular>
              <span v-if="geoDataLoading === true">Bitte warten</span>
              <span v-else>Geo-Daten abgleichen</span>
            </v-btn>
            <v-btn
              color="secondary"
              class="ml-2"
              :disabled="!isOverviewScopeValid || !isLimitValid || debugLoading"
              :loading="debugLoading"
              @click="debugQualification"
            >
              Debug Qualification
            </v-btn>
          </div>
          <div v-if="debugRows && debugRows.length" class="mt-4">
            <v-card class="mb-6">
              <v-simple-table>
                <thead>
                <tr>
                  <th class="text-left column-width">Account-ID</th>
                  <th class="text-left column-width">Approved</th>
                  <th class="text-left column-width">PLZ</th>
                  <th class="text-left column-width">Stadt</th>
                  <th class="text-left column-width">Ländercode</th>
                  <th class="text-left column-width">Straße</th>
                  <th class="text-left column-width">Lat</th>
                  <th class="text-left column-width">Lng</th>
                  <th class="text-left column-width">hasZipCode</th>
                  <th class="text-left column-width">hasCity</th>
                  <th class="text-left column-width">hasCountryCode</th>
                  <th class="text-left column-width">hasStreet</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="row in debugRows" :key="row.id">
                  <td>{{ row.id }}</td>
                  <td>{{ row.isApproved ? 'Ja' : 'Nein' }}</td>
                  <td>{{ row.zipCode || '—' }}</td>
                  <td>{{ row.city || '—' }}</td>
                  <td>{{ row.countryCode || '—' }}</td>
                  <td>{{ row.street || '—' }}</td>
                  <td>{{ row.latitude !== null && row.latitude !== undefined ? row.latitude : '—' }}</td>
                  <td>{{ row.longitude !== null && row.longitude !== undefined ? row.longitude : '—' }}</td>
                  <td>{{ row.hasZipCode ? 'Ja' : 'Nein' }}</td>
                  <td>{{ row.hasCity ? 'Ja' : 'Nein' }}</td>
                  <td>{{ row.hasCountryCode ? 'Ja' : 'Nein' }}</td>
                  <td>{{ row.hasStreet ? 'Ja' : 'Nein' }}</td>
                </tr>
                </tbody>
              </v-simple-table>
            </v-card>
          </div>
          <div v-else-if="debugLoadedOnce && !debugLoading" class="mt-2 grey--text">
            Keine Debug-Daten gefunden (prüfe Scope/Limit).
          </div>
          <v-dialog v-model="geoDataDialog" max-width="1000px">
            <v-card class="pa-4">
              <v-card-title>
      <span class="headline">
        {{ reviseMode === 'dry-run' ? 'Dry-Run Ergebnis' : 'Import-Ergebnis' }}
      </span>
              </v-card-title>
              <v-card-text>
                <!-- Dry-run summary view -->
                <div v-if="reviseMode === 'dry-run'">
                  <div v-if="dryRunSummary && !dryRunSummary.error">
                    <p class="mb-2">
                      <strong>Scope:</strong>
                      Gruppe: {{ dryRunSummary.scope?.group || '—' }},
                      Jahr: {{ dryRunSummary.scope?.year || '—' }}
                    </p>
                    <p class="mb-2">
                      <strong>Counts:</strong>
                      Total: {{ dryRunSummary.counts?.total ?? '—' }},
                      Missing: {{ dryRunSummary.counts?.missing ?? '—' }}
                    </p>
                    <p class="mb-4">
                      <strong>Wird verarbeitet:</strong>
                      {{ dryRunSummary.will_process ?? '—' }}
                      <span v-if="missingOnly === '1'"> (Nur die fehlenden, bei denen ein Geodaten‑Abgleich möglich ist.)</span>
                      <span v-else> (alle Accounts)</span>
                    </p>
                    <p class="grey--text text--darken-1">
                      Hinweis: Beim Import (dry_run=0) werden die Geo-Daten geschrieben.
                    </p>
                  </div>
                  <div v-else>
                    <p class="red--text">
                      Dry-Run fehlgeschlagen: {{ dryRunSummary?.error || 'Unbekannter Fehler' }}
                    </p>
                  </div>
                </div>
                <!-- Import results view -->
                <div v-else>
                  <div class="mb-3" v-if="reviseRunning">
                    <v-progress-linear indeterminate color="blue" class="mb-2"></v-progress-linear>
                    <span>Import läuft … Ergebnisse werden fortlaufend angezeigt.</span>
                  </div>
                  <v-data-table
                    :headers="reviseHeaders"
                    :items="reviseResults"
                    :items-per-page="10"
                    dense
                    class="elevation-1"
                  >
                    <template v-slot:item.status="{ item }">
                      <v-chip :color="item.status === 'updated' ? 'green' : item.status === 'error' ? 'red' : item.status === 'skipped' ? 'grey' : 'blue'"
                              small outlined>
                        {{ item.status }}
                      </v-chip>
                    </template>
                  </v-data-table>

                  <div class="mt-2 grey--text text--darken-1">
                    Zeilen: {{ reviseResults.length }}
                  </div>
                </div>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <!-- Actions for dry-run -->
                <template v-if="reviseMode === 'dry-run'">
                  <v-btn text color="grey" @click="onCloseDialog">Schließen</v-btn>
                  <v-btn color="primary" :disabled="!!dryRunSummary?.error" @click="onImport">
                    Importieren
                  </v-btn>
                </template>
                <!-- Actions for import -->
                <template v-else>
                  <v-btn color="primary" :disabled="reviseRunning" @click="onCloseDialog">
                    Schließen
                  </v-btn>
                </template>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
export default {
  components: {
    //
  },

  data: () => ({
    initiated: false,
    geoDataInformation: [],
    geoDataReviseResult: [],
    geoDataDialog: null,
    geoDataLoading: false,
    loading: false,
    group: '',
    year: '',
    lang: 'de',
    langOptions: [
      { text: 'Deutsch', value: 'de' },
      { text: 'Englisch', value: 'en' }
    ],
    missingOnly: '1',
    missingOptions: [
      { text: 'Nur fehlende', value: '1' },
      { text: 'Alle Accounts', value: '0' }
    ],
    reviseMode: 'idle',
    dryRunSummary: null,
    reviseResults: [],
    reviseRunning: false,
    reviseHeaders: [
      { text: 'Status', value: 'status' },
      { text: 'Account', value: 'accountId' },
      { text: 'Nachricht', value: 'message' }
    ],
    debugLimit: 10,
    debugRows: [],
    debugLoading: false,
    debugLoadedOnce: false
  }),
  methods: {
    getList: async function () {
      this.loading = true
      const response = await this.$http.get('/geo-data/overview', {
        params: {
          group: this.group,
          year: this.year,
          lang: this.lang || 'de'
        }
      })
      this.geoDataInformation = response.data.counts
      this.totals = response.data.totals
      this.loading = false
    },
    bulkRevise: async function () {
      this.loading = true
      this.geoDataLoading = true
      this.geoDataDialog = false
      this.reviseMode = 'idle'
      this.dryRunSummary = null
      this.reviseResults = []
      const url = `/geo-data/revise?group=${this.group}&year=${this.year}&missing_only=${this.missingOnly}&dry_run=1`
      const response = await this.$http.get(url, {
        responseType: 'text'
      })
      const line = typeof response.data === 'string'
        ? JSON.parse(response.data)
        : response.data

      this.dryRunSummary = line
      this.reviseMode = 'dry-run'
      this.geoDataDialog = true
      this.geoDataLoading = false
      this.loading = false
    },
    reload: function () {
      window.location.reload()
    },
    onImport: async function () {
      this.reviseMode = 'import'
      this.reviseResults = []
      this.reviseRunning = true
      const url = `/geo-data/revise?group=${this.group}&year=${this.year}&missing_only=${this.missingOnly}&dry_run=0`
      const response = await this.$http.get(url, {
        responseType: 'text'
      })
      console.log(url)
      const raw = response.data
      const text = typeof raw === 'string'
        ? raw
        : (raw && typeof raw === 'object') ? JSON.stringify(raw) : String(raw || '')
      console.log(text)
      text.split('\n').forEach((line) => {
        const trimmed = line.trim()
        if (trimmed) this.handleStreamLine(trimmed)
      })
      this.reviseRunning = false
    },
    handleStreamLine (line) {
      let obj
      try {
        obj = JSON.parse(line)
      } catch {
        obj = { status: 'raw', message: line }
      }
      this.reviseResults.push({
        status: obj.status || (obj.dry_run ? 'dry_run' : 'info'),
        accountId: obj.accountId ?? obj.id ?? '',
        message: obj.message || obj.details || '',
        _raw: obj
      })
    },
    onCloseDialog () {
      this.geoDataDialog = false
      this.reviseMode = 'idle'
      this.dryRunSummary = null
      this.reviseResults = []
      this.reviseRunning = false
    },
    async debugQualification () {
      this.debugLoading = true
      this.debugLoadedOnce = true
      this.debugRows = []
      const url = `/geo-data/debug-qualification?group=${this.group}&year=${this.year}&limit=${this.debugLimit}`
      const response = await this.$http.get(url)
      this.debugRows = Array.isArray(response.data) ? response.data : []
      this.debugLoading = false
    }
  },
  computed: {
    isOverviewScopeValid () {
      const hasGroup = !!(this.group && this.group.trim().length > 0)
      const hasYear = !!(this.year && this.year.trim().length > 0)
      // Valid when both empty or both filled
      return (hasGroup && hasYear) || (!hasGroup && !hasYear)
    },
    isLimitValid () {
      return Number(this.debugLimit) > 0
    }
  },
  async created () {
    await this.$auth.waitForAuth()
    if (!this.$auth.isAuthenticated) {
      await this.$router.push('/')
      return
    }

    await this.getList()
    this.initiated = true
  }
}
</script>
<style>
.controls-inline {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: space-between;
  gap: 100px;
  width: 100%;
}
.controls-inline .v-text-field,
.controls-inline .v-select {
  flex: 0 0 auto;
  max-width: 150px;
}
.controls-inline .ml-auto {
  margin-left: auto;
}
.controls-inline .v-btn {
  flex: 0 0 auto;
}

</style>
